import {
  type CurrentPreviousAndNextGamesRes,
  OrganizationType,
  type ScoreboardResponseRelatedScheduleResponseRelatedPagedData,
  type SportFilter,
  v1OrganizationsOpponentsRecentScoresDetail,
  type V1OrganizationsOpponentsRecentScoresDetailParams,
  v2OrganizationsPrevNextDetail,
  type V2OrganizationsScheduleDetailParams,
} from '@on3/api';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { baseSports } from '@on3/ui-lib/store/mocks/sports';
import { type IPageProps } from '@on3/ui-lib/types/common-props';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { getFulfilledValue, webUrl } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import {
  getHighSchoolSportLabel,
  getIdFromParam,
  getSportKeyFromParam,
} from '@on3/ui-lib/utils/url';
import { BreadCrumb } from '@public/components/BreadCrumb/BreadCrumb';
import { HighSchoolScoresPage as Page } from '@public/components/HighSchool/Scores/Page/Single';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { AdminLink } from '@public/components/User/AdminLink';
import { createDescription, createTitle } from '@public/utils/meta';
import { type AxiosError } from 'axios';
import { type GetServerSideProps } from 'next';

interface HighSchoolScoresPageProps extends IPageProps {
  pageData: {
    sport: SportFilter;
  };
  scores: CurrentPreviousAndNextGamesRes;
  opponentScores: ScoreboardResponseRelatedScheduleResponseRelatedPagedData;
  filters: {
    sports: SportFilter[];
  };
}

const HighSchoolScoresPage = (props: HighSchoolScoresPageProps) => {
  const { scores, opponentScores, pageData, filters } = props;
  const schoolName =
    opponentScores?.relatedModel?.organization?.name || 'High School';
  const schoolUrl = opponentScores?.relatedModel?.organization?.urlSlug;
  const sport = opponentScores?.relatedModel?.sport?.name;
  const year = opponentScores?.relatedModel?.year.toString();
  const primaryColor = adjustColorForReadability(
    opponentScores?.relatedModel?.organization?.primaryColor ?? '#fe3b1f',
  );

  const sportLabel = pageData?.sport
    ? getHighSchoolSportLabel(pageData.sport)
    : '';

  const title = createTitle(schoolName, sportLabel, 'Scores');

  const description = createDescription(
    `View On3's latest scores and schedules for {0} {1}.`,
    schoolName,
    sportLabel,
  );
  const canonical = `${webUrl}/high-school/${schoolUrl}/${slugify(
    sport || 'football',
  )}/scores/`;

  const noIndex = (opponentScores.list || []).length === 0 && !scores;

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <AdSidebarLayout>
        <AdminLink
          path={`/organization/${schoolUrl}/schedule/`}
          query={{ year, sport }}
          title="Edit Schedule"
        />
        <BreadCrumb
          color={primaryColor}
          crumbs={[
            { title: 'High School', href: '/high-school/' },
            { title: schoolName, href: `/high-school/${schoolUrl}` },
          ]}
          title={title}
        />
        <Page
          filters={filters}
          opponentScores={opponentScores}
          scores={scores}
          title={title}
        />
      </AdSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const highSchoolSiteKey = 402;
  const siteData = await withValidUrl.isValid(ctx, highSchoolSiteKey);
  const userData = await withAuthentication.isAuth(ctx);
  const { sport = 'football', id } = ctx.query as { id: string; sport: string };
  const { rankingSports = [] } = siteData || {};

  const orgKey = getIdFromParam(id);
  const sportKey = getSportKeyFromParam(sport);

  if (!siteData || !orgKey || !sportKey) {
    return {
      notFound: true,
    };
  }

  const baseParams = {
    sportKey,
    year: siteData.defaultYears.highSchoolYear,
  };

  const scoreParams: V2OrganizationsScheduleDetailParams = {
    orgKey,
    ...baseParams,
  };

  const opponentScoresParams: V1OrganizationsOpponentsRecentScoresDetailParams =
    {
      organizationKey: orgKey,
      ...baseParams,
    };

  try {
    const [scores, opponentScores] = await Promise.allSettled([
      v2OrganizationsPrevNextDetail(proxyApi, scoreParams),
      v1OrganizationsOpponentsRecentScoresDetail(
        proxyApi,
        opponentScoresParams,
      ),
    ]);

    const opponentScoresData = getFulfilledValue(opponentScores);

    const { relatedModel } = opponentScoresData || {};

    const orgType = relatedModel?.organization?.orgTypeEnum;
    const orgSlug = relatedModel?.organization?.urlSlug;

    if (orgType !== OrganizationType.HighSchool) {
      return { notFound: true };
    }

    if (id !== orgSlug) {
      return {
        redirect: {
          permanent: true,
          destination: `/high-school/${orgSlug}/${sport}/scores/`,
        },
      };
    }

    const data = {
      pageType: 'database',
      siteData,
      userData,
      pageData: {
        sport: rankingSports?.find((s) => slugify(s.name) === sport),
      },
      scores: getFulfilledValue(scores),
      opponentScores: opponentScoresData,
      filters: {
        sports: baseSports,
      },
    };

    return { props: { ...data } };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.error(err);

    return { props: {} };
  }
};

export default withValidUrl(HighSchoolScoresPage);
